<script>
export default {
  name: 'CreateWorkItemForm',
  render: () => null
}
</script>

<script setup>
import { isEmpty } from 'lodash'
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

import ObjectivesInfoApiHandler from '@/api/okr-elements'
import { tracker } from '@/tracking/amplitude'
import { EVENT_CATEGORIES } from '@/tracking/amplitude-helpers'
import { trackWorkItemLinkedEvent } from '@/tracking/gtm-helpers'
import { useCreateWorkItem } from '@/utils/azure-devops-helpers/work-item'
import {
  ASSIGNEE_ENTITY_KEYS,
  COMPANY_SETTINGS_ENTITY_KEYS,
  OKR_ELEMENT_ENTITY_KEYS
} from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { OBJECTIVE_TYPES } from '@/utils/objective-types'
import { createGroupsList } from '@/utils/objectives'
import { useElementOwner } from '@/utils/okr-elements-forms-helpers'
import { getNavigationTabName } from '@/utils/tracking'
import { OKR_ELEMENT_KEYS } from '@/utils/view-row-actions'

const emit = defineEmits({
  save: null
})

const props = defineProps({
  source: {
    type: String,
    default: ''
  }
})

const api = new ObjectivesInfoApiHandler()

const store = useStore()

const userData = computed(() => store.state.system.userData)

const settings = computed(() => store.state.system.settings)

const shouldContributeByDefault = computed(
  () => settings.value[COMPANY_SETTINGS_ENTITY_KEYS.TASK_CONTRIBUTE_BY_DEFAULT]
)

const route = useRoute()

const onWorkItemCreated = async (parentElement, createdWorkItem) => {
  const ownerId = userData.value?.[ASSIGNEE_ENTITY_KEYS.USER_ACCOUNT_ID] || null
  const groups =
    store.getters['system/userGroupsByWorkspace'](parentElement[OKR_ELEMENT_KEYS.WORKSPACE_ID]) ||
    []

  const { createUsersListForPayload } = useElementOwner({
    [OKR_ELEMENT_ENTITY_KEYS.TYPE_ID]: OBJECTIVE_TYPES.WORK_ITEM
  })

  const payload = {
    contribute: shouldContributeByDefault.value,
    platformElementIds: [createdWorkItem[OKR_ELEMENT_KEYS.ID]],
    parentId: parentElement[OKR_ELEMENT_KEYS.ID],
    users: createUsersListForPayload({ ownerId }),
    groups: createGroupsList(groups),
    workspaceId: parentElement[OKR_ELEMENT_KEYS.WORKSPACE_ID],
    dueDate: parentElement[OKR_ELEMENT_KEYS.DUE_DATE],
    intervalId: parentElement[OKR_ELEMENT_KEYS.INTERVAL_ID],
    levelId: store.getters['objectives/getLevelItem'](
      OKR_ELEMENT_KEYS.TYPE_ID,
      OBJECTIVE_TYPES.WORK_ITEM,
      {
        [OKR_ELEMENT_KEYS.WORKSPACE_ID]: parentElement[OKR_ELEMENT_KEYS.WORKSPACE_ID],
        [OKR_ELEMENT_KEYS.TYPE_ID]: OBJECTIVE_TYPES.WORK_ITEM
      }
    )[OKR_ELEMENT_KEYS.ID],
    targetValue: undefined,
    originalValue: undefined,
    currentValue: undefined
  }

  try {
    const result = await api.createOkrElement(payload)

    if (isEmpty(result.elements)) return

    const [firstLinkedWorkItem] = result.elements || []

    emit('save', [result])

    if (firstLinkedWorkItem) {
      tracker.logEvent('Linked Work item', {
        category: EVENT_CATEGORIES.OKR_MANAGEMENT,
        label: firstLinkedWorkItem.issueType,
        event_ref: 'Plugin',
        source: props.source,
        contribution: firstLinkedWorkItem[OKR_ELEMENT_KEYS.CONTRIBUTE] ? 'Yes' : 'No',
        tab: getNavigationTabName(route),
        ent_id: firstLinkedWorkItem[OKR_ELEMENT_KEYS.DISPLAY_ID]
      })

      trackWorkItemLinkedEvent()
    }
  } catch (error) {
    handleError({ error })
  }
}

const { createWorkItem } = useCreateWorkItem()

const create = async (parentElement = {}, { workItemKindId } = {}) => {
  const createdWorkItem = await createWorkItem(workItemKindId)

  if (createdWorkItem) {
    await onWorkItemCreated(parentElement, createdWorkItem)
  }
}

defineExpose({
  create
})
</script>
